import { Alert, Button, Drawer, NativeSelect, SegmentedControl, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "../../api/auth";
import { useTodayChecks } from "../../api/checks";
import { useErrorText } from "../../api/errors";
import { useDepartments, useUsers } from "../../api/management";
import { taskKinds, timeWindows, useCreateTask, type TaskKind, type TaskTimeWindow } from "../../api/tasks";

/** Jednorázový úkol s datem a přiřazením (F-TSK-06). */
export function NewTaskDrawer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const me = useCurrentUser();
  const canPickUser = me.permissions.includes("UsersManage");
  const departments = useDepartments();
  const users = useUsers(canPickUser);
  const today = useTodayChecks().data?.businessDate;
  const create = useCreateTask();
  const form = useForm({
    initialValues: {
      title: "",
      description: "",
      when: "today" as "today" | "tomorrow" | "date",
      date: "",
      timeWindow: "Any" as TaskTimeWindow,
      kind: "Work" as TaskKind,
      assignee: me.departments[0] ? `d:${me.departments[0].id}` : "none",
    },
    validate: { title: (v) => (!v.trim() ? t("error.tasks.title_required") : null) },
  });

  const resolveDate = (values: typeof form.values) => {
    if (values.when === "date") return values.date;
    const base = new Date(`${today}T12:00:00`);
    if (values.when === "tomorrow") base.setDate(base.getDate() + 1);
    return base.toISOString().slice(0, 10);
  };

  const assigneeOptions = [
    { value: "none", label: t("tasks.unassigned") },
    ...(departments.data?.map((d) => ({ value: `d:${d.id}`, label: d.name })) ?? []),
    ...(canPickUser ? (users.data?.filter((u) => u.isActive).map((u) => ({ value: `u:${u.id}`, label: u.displayName })) ?? []) : []),
  ];

  return (
    <Drawer opened onClose={onClose} position="bottom" title={<Text fw={700} size="lg">{t("tasks.new")}</Text>}
      styles={{ content: { flex: "0 0 auto", width: "100%", height: "auto", maxHeight: "92dvh", borderRadius: "16px 16px 0 0" } }}>
      <form
        onSubmit={form.onSubmit((values) => {
          const [type, id] = values.assignee.split(":");
          create.mutate(
            {
              title: values.title.trim(),
              description: values.description.trim() || null,
              date: resolveDate(values),
              timeWindow: values.timeWindow,
              kind: values.kind,
              assigneeUserId: type === "u" ? id! : null,
              assigneeDepartmentId: type === "d" ? id! : null,
            },
            { onSuccess: onClose },
          );
        })}
      >
        <Stack pb="md">
          <TextInput label={t("tasks.form.title")} required {...form.getInputProps("title")} />
          <Textarea label={t("tasks.form.description")} autosize minRows={2} {...form.getInputProps("description")} />
          <SegmentedControl fullWidth {...form.getInputProps("when")}
            data={[
              { value: "today", label: t("check.today") },
              { value: "tomorrow", label: t("check.tomorrow") },
              { value: "date", label: t("tasks.form.otherDate") },
            ]} />
          {form.values.when === "date" && <TextInput type="date" min={today} {...form.getInputProps("date")} />}
          <NativeSelect size="lg" label={t("tasks.form.timeWindow")} {...form.getInputProps("timeWindow")}
            data={timeWindows.map((w) => ({ value: w, label: t(`tasks.window.${w}`) }))} />
          <NativeSelect size="lg" label={t("tasks.form.kind")} {...form.getInputProps("kind")}
            data={taskKinds.map((k) => ({ value: k, label: t(`tasks.kind.${k}`) }))} />
          <NativeSelect size="lg" label={t("tasks.form.assignee")} {...form.getInputProps("assignee")} data={assigneeOptions} />
          {create.isError && <Alert color="red">{errorText(create.error)}</Alert>}
          <Button type="submit" size="lg" loading={create.isPending}>
            {t("common.save")}
          </Button>
        </Stack>
      </form>
    </Drawer>
  );
}
